import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useObservedQuery } from './use-observed-query';
import { LEGACY_DEFAULT_ASSISTANT_NAME, MAESTRO_DEFAULT, useAgentName } from './agent-name-context';
import { useTranslation } from './i18n';
import type { Conversation } from './conversations';
import { clearLostGroup, groupChatAction, handleLostAccess, markGroupEnded, useGroupChatStore } from './group-chat-api';
import { queryClient } from './query-client';
import { useGroupChatEnabled } from './group-chat-flag';
import {
  endedSummary,
  groupMaestroName,
  groupSummaryFromMembers,
  nameMaestros,
  parseGroupInfo,
  parseGroupMaestro,
  parseGroupMember,
  parseViewerState,
  rowGroupSummary,
  type GroupMaestroWire,
  type GroupMember,
  type GroupRole,
  type GroupSummary,
  type GroupViewerState,
} from './group-chat';
import { parseInvite, type InviteWire } from './group-chat-actions';
import { parseGroupCapabilities, parseGroupDetails, type GroupCapabilities, type GroupDetails } from './group-admin';
import { useChatsRow, useConversationRowMissing } from './use-conversation-row';
import { notificationsFromRow, parseGroupNotifications, type GroupNotifications } from './group-notifications';
import { seedPresenceFromMembers } from './group-live-state';

export interface GroupMembersRead {
  summary: GroupSummary | null;
  members: GroupMember[];
  /** Phase 5: every Maestro in the group, by owner (absent from a symphony-bff that predates it → []). */
  maestros: GroupMaestroWire[];
  pendingInvites: InviteWire[];
  yourRole: GroupRole | null;
  /** Phase 10a: what the caller may do here (derived from the role when the service sent none); null on a refusal. */
  capabilities: GroupCapabilities | null;
  /** Phase 10a: the group's name, description, photo and settings. */
  details: GroupDetails | null;
  /** The caller's own notification setting here; null from a symphony-bff that predates it. */
  notifications: GroupNotifications | null;
  refusal: string | null;
}

export const groupMembersKey = (conversationId: string | null | undefined) => ['group-members', conversationId ?? null] as const;

/**
 * The `members` read (roles + pending invites for admins). NOT_A_MEMBER clears the chat; GROUP_ENDED — or a group that
 * answers `active: false` — marks it ended (it stays a group, `markGroupEnded`).
 */
export async function fetchGroupMembers(conversationId: string): Promise<GroupMembersRead> {
  const outcome = await groupChatAction('members', { conversationId });
  if (outcome.kind === 'refused') {
    handleLostAccess(conversationId, outcome.refusal);
    return { summary: null, members: [], maestros: [], pendingInvites: [], yourRole: null, capabilities: null, details: null, notifications: null, refusal: outcome.refusal };
  }
  if (outcome.kind !== 'ok') throw new Error('group_members_failed');
  clearLostGroup(conversationId);
  const d = outcome.data;
  const members = (Array.isArray(d.members) ? d.members : []).map(parseGroupMember).filter((m): m is GroupMember => !!m);
  // Design §21: each other member's online / last seen, as the caller may see it (frames keep it current after).
  seedPresenceFromMembers(d.members);
  const yourRole = d.yourRole === 'OWNER' || d.yourRole === 'ADMIN' || d.yourRole === 'MEMBER' ? d.yourRole : null;
  const maestros = (Array.isArray(d.maestros) ? d.maestros : []).map(parseGroupMaestro).filter((m): m is GroupMaestroWire => !!m);
  const info = parseGroupInfo(d.group);
  // Who was in it and is not now (an ended group: everyone but its owner) — named, never counted (`formerParticipants`).
  const formerMembers = (Array.isArray(d.formerMembers) ? d.formerMembers : []).map(parseGroupMember).filter((m): m is GroupMember => !!m);
  const summary = groupSummaryFromMembers(info, members, yourRole, maestros, formerMembers);
  if (info && !info.active) markGroupEnded(conversationId, summary);
  return {
    summary,
    members,
    maestros,
    pendingInvites: (Array.isArray(d.pendingInvites) ? d.pendingInvites : []).map(parseInvite).filter((i): i is InviteWire => !!i && i.state === 'PENDING'),
    yourRole,
    capabilities: parseGroupCapabilities(d.yourCapabilities, yourRole),
    details: parseGroupDetails(d.group),
    notifications: parseGroupNotifications(d.yourNotifications),
    refusal: null,
  };
}

/**
 * Read `members` NOW and keep the answer where every reader looks (the `members` cache `useGroupSummary` reads) —
 * resolves once it is stored. "New group" awaits it before offering "Open group", so the chat opens as the group
 * (its header, "Message the group", names) and never as the private chat a list that has not re-read yet would draw.
 */
export async function loadGroupSummary(conversationId: string): Promise<GroupSummary | null> {
  try {
    const read = await queryClient.fetchQuery({ queryKey: groupMembersKey(conversationId), queryFn: () => fetchGroupMembers(conversationId), staleTime: 0 });
    return read.summary;
  } catch {
    return null;
  }
}

/**
 * Opening a group chat re-reads its participants (`members`) even when the cache holds an answer younger than its
 * stale time: a Maestro or person added while this browser was elsewhere is in the header, the `@` picker and the
 * author labels from the first frame of the chat. An in-flight read is kept, not restarted. iOS / Android re-read on
 * open the same.
 */
export function membersReadOnOpen(conversationId: string): void {
  void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) }, { cancelRefetch: false });
}

export function cachedMembersSummary(conversationId: string): GroupSummary | null {
  return queryClient.getQueryData<GroupMembersRead>(groupMembersKey(conversationId))?.summary ?? null;
}

export function useGroupMembers(conversationId: string | null | undefined, enabled: boolean) {
  // The singleton client (see `useObservedQuery`): the chat header reads this with no provider above it.
  return useObservedQuery({
    queryKey: groupMembersKey(conversationId),
    queryFn: () => fetchGroupMembers(conversationId as string),
    enabled: enabled && !!conversationId,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * The open conversation's group summary, behind `symphonyGroupChat`: the row's own (`group`, folded in from the
 * chats-v1 list), else — for a group the row knows only as `groupChat: true`, or a conversation this account cannot
 * read by id (a group in someone else's account, opened from a push or a link) — the same summary built from
 * `members`. Null for a private chat, and always null with the flag off.
 */
export function useGroupSummary(conversationId: string | null | undefined, row: Conversation | undefined): {
  enabled: boolean;
  summary: GroupSummary | null;
  viewerState: GroupViewerState | null;
} {
  const enabled = useGroupChatEnabled();
  const { short: ownAgentName } = useAgentName();
  const { t } = useTranslation();
  const { forcedGroups, lost, ended } = useGroupChatStore();
  // A chat the harness answered GROUP_CHAT_POST_ONLY for is a group even before any row says so (Phase 4) — and
  // regardless of the client flag: the server's refusal proves it, and a member without the flag would be stuck.
  const forced = !!conversationId && !!forcedGroups[conversationId];
  const on = enabled || forced;
  const fromRow = useMemo(() => (on ? rowGroupSummary(row) : null), [on, row]);
  const missing = useConversationRowMissing(conversationId ?? undefined);
  const endedMark = conversationId ? ended[conversationId] : undefined;
  // A group the server already refused this viewer for is not re-read on every render (the chat shows the lost state).
  // Every open group reads `members` — the list row's participants can be stale (2026-11-01 hands-on: a Maestro another
  // member added stayed missing from the header, the `@` highlighting and its author label, even after reopening).
  const needsMembers = on && !!conversationId && !lost[conversationId] && !endedMark && (!!fromRow || row?.groupChat === true || forced || (!row && missing));
  const members = useGroupMembers(conversationId, needsMembers);
  // ...and OPENING the chat re-reads it, whatever the cache holds (`membersReadOnOpen`).
  const readOnOpen = useRef<string | null>(null);
  useEffect(() => {
    if (!needsMembers || !conversationId || readOnOpen.current === conversationId) return;
    readOnOpen.current = conversationId;
    membersReadOnOpen(conversationId);
  }, [conversationId, needsMembers]);
  // A `members` read this browser holds (the header's, the composer's, the `@` picker's re-read, "New group"'s) is the
  // freshest view of WHO is in the group, so it wins over the list row: a Maestro added after the chat opened reaches
  // the picker, and a chat opened right after "New group" is a group before the list has re-read.
  const fromMembers = on ? members.data?.summary ?? null : null;
  // Ended (2026-10-31): still a group — the same summary, flagged, so the chat draws no composer.
  const raw = useMemo(() => {
    const base = fromMembers ?? fromRow ?? (on && endedMark && endedMark !== true ? endedMark : null);
    return base && endedMark ? endedSummary(base) : base;
  }, [fromMembers, fromRow, on, endedMark]);
  // A Maestro with no name on the wire: the viewer's own agent name, else "Dana's Maestro" / "Maestro" (Phase 5).
  const summary = useMemo(() => (raw ? nameMaestros(raw, ownAgentName, t) : null), [raw, ownAgentName, t]);
  const viewerState = useMemo(() => (on && summary ? parseViewerState(row?.viewerState) : null), [on, summary, row?.viewerState]);
  return { enabled, summary, viewerState };
}

/**
 * `nameMaestros` as a function, for every surface that parses a summary of its own (the sidebar, search, starred):
 * a Maestro the server sent with no name reads as the viewer's own agent name, "Dana's Maestro" or "Maestro" there
 * too — the same label as inside the chat, never an empty one.
 */
export function useMaestroNamer(): (summary: GroupSummary | null) => GroupSummary | null {
  const { short: ownAgentName } = useAgentName();
  const { t } = useTranslation();
  return useCallback((summary: GroupSummary | null) => (summary ? nameMaestros(summary, ownAgentName, t) : null), [ownAgentName, t]);
}

/**
 * The caller's notification setting for a group, behind `symphonyGroupChat`: the `members` answer when this browser
 * has one (never fetched from here), else the chats-v1 row. Null for an old symphony-bff, a private chat, or flag off.
 */
export function useGroupNotifications(conversationId: string | null | undefined): GroupNotifications | null {
  const enabled = useGroupChatEnabled();
  const members = useGroupMembers(conversationId, false);
  const row = useChatsRow(conversationId ?? undefined);
  if (!enabled || !conversationId) return null;
  return members.data?.notifications ?? notificationsFromRow(row);
}

/**
 * `{maestroAgentName}` in a group's Notifications picker and hint: the viewer's own Maestro as THIS group names it
 * (`groupMaestroName` — "Marco's Maestro", or the name the viewer gave it), from the `members` answer this browser holds,
 * else the chats-v1 row; the viewer's own agent name when the group holds none.
 */
export function useGroupMaestroName(conversationId: string | null | undefined): string {
  const { short: ownAgentName } = useAgentName();
  const { t } = useTranslation();
  const members = useGroupMembers(conversationId, false);
  const row = useChatsRow(conversationId ?? undefined);
  return groupMaestroName(members.data?.summary ?? rowGroupSummary(row), ownAgentName, t, [MAESTRO_DEFAULT.short, MAESTRO_DEFAULT.name, LEGACY_DEFAULT_ASSISTANT_NAME]);
}
